'use client';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import type { LoadedPlayerJourney } from '../../libs/loadPlayerJourney';
import { eventPlayerHref } from '../../libs/eventPlayers';
import { JourneyFixtureCard, type JourneyContext } from './JourneyFixtureCard';
import { JourneyShareButton } from './JourneyShareButton';
import { useJourneyRefresh } from './useJourneyRefresh';
export function PersonalJourneyCard({
  data,
  registrationOpen,
  ...context
}: JourneyContext & { data: LoadedPlayerJourney; registrationOpen: boolean }) {
  const t = useTranslations('Event.playerJourney');
  const j = data.journey;
  useJourneyRefresh(context.eventId, !!j && !data.competitionHidden);
  const href = `/${context.locale}${eventPlayerHref(context.eventId, context.slug, data.player.playerId, data.division.id)}`;
  return (
    <section
      className="bg-card border-border mb-6 rounded-lg border p-5"
      aria-label={t('yourEvent')}
    >
      <p className="text-muted text-xs">
        {t('yourEvent')} · {data.division.name}
      </p>
      <h2 className="font-heading mt-1 mb-3 text-xl font-bold">
        {t(
          j?.state === 'finished'
            ? 'finished'
            : j?.next
              ? j.run.length
                ? 'nextMatch'
                : 'firstMatch'
              : j?.state === 'awaiting'
                ? 'resultPending'
                : 'registered',
        )}
      </h2>
      {j?.next ? (
        <JourneyFixtureCard
          {...context}
          fixture={j.next}
          opponents={data.opponents}
          division={data.division.id}
        />
      ) : j?.state === 'finished' ? (
        <p className="text-foreground text-lg">
          {j.finish
            ? t(j.finish.best === j.finish.worst ? 'place' : 'range', {
                place: j.finish.best,
                best: j.finish.best,
                worst: j.finish.worst,
              })
            : t('placementUnknown')}
        </p>
      ) : (
        <p className="text-muted text-sm">
          {t(
            data.competitionHidden
              ? 'competitionUnavailable'
              : j?.state === 'awaiting'
                ? 'resultPending'
                : 'drawPending',
          )}
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <Link
          href={href}
          className="text-foreground inline-flex min-h-11 items-center font-semibold underline underline-offset-4"
        >
          {t('yourRun')}
        </Link>
        <Link
          href="#event-register"
          className="text-muted inline-flex min-h-11 items-center text-sm underline underline-offset-4"
        >
          {t(registrationOpen ? 'manageEntry' : 'viewEntry')}
        </Link>
        {j?.state === 'finished' && <JourneyShareButton href={href} />}
      </div>
      {data.divisions.length > 1 && (
        <nav aria-label={t('division')} className="text-muted mt-2 flex flex-wrap gap-4 text-sm">
          {data.divisions
            .filter((d) => d.id !== data.division.id)
            .map((d) => (
              <Link
                key={d.id}
                className="inline-flex min-h-11 items-center underline underline-offset-4"
                href={`/${context.locale}${eventPlayerHref(context.eventId, context.slug, data.player.playerId, d.id)}`}
              >
                {d.name}
              </Link>
            ))}
        </nav>
      )}
    </section>
  );
}
